import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import GameLayout from "../../layout/GameLayout";
import FloatingCard from "../../ui/FloatingCard";
import FloatingButton from "../../ui/FloatingButton";
import ConfirmModal from "../../ui/ConfirmModal";
import { useWebSocket, useMatchWebSocket } from "../../../contexts/WebSocketContext";
import { useAuth } from "../../../contexts/AuthContext";
import { debugLog } from "../../../utils/debugLog";
import LiveMatchHeader from "./components/LiveMatchHeader";
import LiveScoreboard from "./components/LiveScoreboard";
import ScoreFullscreen from "./components/ScoreFullscreen";
import LiveEventFeed from "./components/LiveEventFeed";
import LiveMatchEventModal from "./modals/LiveMatchEventModal";
import AdvancedScoringModal from "./components/AdvancedScoringModal";
import PlayerActionPopover from "./components/PlayerActionPopover";
import LineupContext from "./LineupContext";
import { canSubstitute, countSubstitutions } from "./utils/lineupUtils";
import { gameStateFromScoreData, newClientEventId } from "./utils/liveScoreState";
import { getSportConfigKey } from "../../../config/sportsConfig";
import { getParticipantName } from "./utils/matchDisplayUtils";
import { validateScoreIncrease, getMatchScoringRules } from "./utils/scoreValidationUtils";
import { getSportScoring } from "./scoring";
import { sportHasAdvancedScoring } from "../../../config/sportsMetrics";
import { getSportScoreboardComponent } from "../scoreboards";
import { sportManagesOwnGames, sportAllowsDraw } from "../../../utils/sportRules";
import {
  useLiveMatchPermissions,
  useLiveMatchTimer,
  useLiveMatchWebSocket,
  useLiveMatchData,
} from "./hooks";
import useLiveMatchPlayers from "./hooks/useLiveMatchPlayers";
import useLiveMatchLineup from "./hooks/useLiveMatchLineup";
import useScoreAnnouncer from "./hooks/useScoreAnnouncer";
import "../../../styles/features/matches.css";

const LiveMatch = ({
  matchId: propMatchId,
  onClose,
  isReadOnly = false,
  token,
  onLogout,
}) => {
  const { matchId: urlMatchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const matchId = propMatchId || urlMatchId;

  const doNavigateBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, {
        state: { restoreTab: location.state.tab },
      });
    } else {
      navigate(-1);
    }
  };

  const handleBack = () => {
    // Full logic with isTimeoutActive is defined after useLiveMatchTimer — see below
    doNavigateBack();
  };
  
  // Authentication and permissions
  const { user, isAuthenticated } = useAuth();
  const currentUserId = user?.id;
  const userRole = user?.role;
  const [isMatchActive, setIsMatchActive] = useState(false);

  // Back-navigation dialog (shown when match clock is running and user tries to leave)
  const [backNavModal, setBackNavModal] = useState({ isOpen: false });

  // Game state for multi-game sports (e.g. badminton Bo3/Bo5).
  // Set by the editor when a game completes; synced to viewers via WebSocket.
  const [liveGameState, setLiveGameState] = useState(null);

  // Scoring mode: 'basic' | 'advanced' — persisted per match in localStorage
  const scoringModeKey = `scoring_mode_${matchId}`;
  const [scoringMode, setScoringModeRaw] = useState(
    () => localStorage.getItem(scoringModeKey) || 'basic'
  );
  const setScoringMode = (mode) => {
    localStorage.setItem(scoringModeKey, mode);
    setScoringModeRaw(mode);
  };

  // Advanced scoring modal state
  const [advancedModal, setAdvancedModal] = useState({
    isOpen: false,
    participant: null,
    preSelectedPlayer: null,
  });
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDanger: false,
    onConfirm: null,
    onCancel: null,
  });

  // Tie-breaker modal state
  const [tieModal, setTieModal] = useState({ isOpen: false, onResolve: null, p1Name: '', p2Name: '', score: '' });

  // Forfeit modal state
  const [forfeitModal, setForfeitModal] = useState({ isOpen: false });

  // Back-nav guard: show dialog only when live match clock is running
  const handleCancelMatchWithModal = () => {
    setConfirmModal({
      isOpen: true,
      title: '❌ Cancel Match',
      message: 'Are you sure you want to cancel this match? This action cannot be undone.',
      confirmText: 'Yes, Cancel Match',
      cancelText: 'Keep Playing',
      isDanger: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        await handleCancelMatch();
        doNavigateBack();
      },
      onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
    });
  };

  const handleForfeitWithModal = () => {
    setForfeitModal({ isOpen: true });
  };
  
  // WebSocket integration
  const { 
    socket, 
    connected, 
    connectionState, 
    joinMatch, 
    leaveMatch,
    connect,
    getConnectionStatus
  } = useWebSocket();

  // Load match data first so we have the integer match.id for WebSocket room joining
  const {
    match,
    loading,
    error,
    setError,
    showEventForm,
    setShowEventForm,
    scoringTemplate,
    eventData,
    setEventData,
    currentScore,
    setCurrentScore,
    eventLog,
    setEventLog,
    handleStartMatch,
    handleCompleteMatch,
    handleScoreUpdate,
    handleAdvancedScoreUpdate,
    handleSubstitutionEvent,
    handleScoreDecrease,
    handleResetScore,
    handleCancelMatch,
    handleForfeitMatch,
    handleAddEvent,
    handleSendEvent,
    getEventTypes,
    isScoreSyncPending,
    syncServerScore,
    adoptServerState,
    setScoreContext,
    serverGameState,
    clockState,
    setClockState,
    postClock,
    currentClockMs,
  } = useLiveMatchData({
    matchId,
    socket,
    connected,
    isMatchActive,
    setIsMatchActive,
  });

  // Integer DB id — used for WebSocket room joining and event filtering so
  // rooms are consistent regardless of whether the URL uses UUID or short_code.
  const numericMatchId = match?.id;

  const { 
    matchEvents,
    liveScore,
    lastScoreBroadcast,
    clockState: wsClockState,
  } = useMatchWebSocket(matchId, numericMatchId);

  // Server match clock broadcasts (LS-B09) replace the local copy
  useEffect(() => {
    if (wsClockState) setClockState(wsClockState);
  }, [wsClockState, setClockState]);

  const { isReadOnlyMode, canEditScore } = useLiveMatchPermissions({
    isReadOnly,
    match,
    isAuthenticated,
    userRole,
    currentUserId,
  });

  // Basketball / football boards run (and store) their own period clock
  const sportKeyForClock = getSportConfigKey(match?.sport_name || match?.sportName);
  const boardOwnsClock = sportKeyForClock === 'basketball' || sportKeyForClock === 'football';
  const { matchDuration, isTimeoutActive, setIsTimeoutActive, stopTimer } = useLiveMatchTimer({
    match,
    serverClock: boardOwnsClock ? null : clockState,
    canControl: !isReadOnlyMode && canEditScore(),
    onClockChange: postClock,
    managesOwnClock: boardOwnsClock,
  });

  // Override handleBack now that isTimeoutActive is available:
  // show a dialog instead of navigating away while the match clock is running.
  // Only editors (canEditScore) see the dialog — viewers/players navigate freely.
  // eslint-disable-next-line no-func-assign
  const handleBackWithDialog = () => {
    if (isMatchActive && !isTimeoutActive && canEditScore()) {
      setBackNavModal({ isOpen: true });
      return;
    }
    doNavigateBack();
  };

  // Advanced scoring — player rosters (fetched only when advanced mode is active)
  const sportName = match?.sport_name || match?.sportName;
  const sportScorer = getSportScoring(sportName);
  const advancedSupported = sportHasAdvancedScoring(sportName);
  const { p1Players, p2Players, loadingPlayers, playersError } = useLiveMatchPlayers(
    match,
    scoringMode === 'advanced' && advancedSupported
  );

  // On-court / bench lineup and substitutions (volleyball, basketball, football)
  const canEditNow = !isReadOnlyMode && canEditScore();
  const {
    rules: subRules,
    lineups,
    substitute,
    startNewSet,
    error: lineupError,
    clearError: clearLineupError,
  } = useLiveMatchLineup({
    match,
    sportName,
    p1Players,
    p2Players,
    active: scoringMode === 'advanced' && advancedSupported,
    canEdit: canEditNow,
    matchEvents,
  });
  const canSwap = subRules.enabled && canEditNow && isMatchActive;

  // Action popover anchored to a player icon: { participant, player, anchorKey }
  const [popover, setPopover] = useState(null);
  // Swap in progress: { participant, inId } (bench tapped) or { participant, outId } ("Sub out")
  const [swapState, setSwapState] = useState(null);
  const [lineupMessage, setLineupMessage] = useState(null);
  const lineupMessageTimer = useRef(null);

  const showLineupMessage = useCallback((participant, text) => {
    clearTimeout(lineupMessageTimer.current);
    setLineupMessage({ participant, text });
    lineupMessageTimer.current = setTimeout(() => setLineupMessage(null), 4000);
  }, []);
  useEffect(() => () => clearTimeout(lineupMessageTimer.current), []);

  useEffect(() => {
    if (!lineupError) return;
    setError(lineupError);
    clearLineupError();
  }, [lineupError, setError, clearLineupError]);

  const sidePlayers = useCallback(
    (participant) => (participant === 'participant1' ? p1Players : p2Players),
    [p1Players, p2Players]
  );
  const findPlayer = useCallback(
    (participant, id) => sidePlayers(participant).find((p) => String(p.id ?? p.userId) === String(id)),
    [sidePlayers]
  );
  // Players who can record stats: everyone on court (all players when there is no lineup)
  const courtPlayers = useCallback((participant) => {
    const lineup = lineups[participant];
    if (!lineup) return sidePlayers(participant);
    return lineup.onCourt.map((id) => findPlayer(participant, id)).filter(Boolean);
  }, [lineups, sidePlayers, findPlayer]);

  // Score announcer (Web Speech API / TTS) — viewers default to muted so a
  // live match doesn't start talking unexpectedly; they can unmute it.
  const { muted, announce, repeat, toggleMute } = useScoreAnnouncer(isReadOnlyMode);

  // Compute expected new score for the scoring participant and announce it
  const announceScoreChange = useCallback((participant, delta, isCorrection) => {
    const opponent = participant === 'participant1' ? 'participant2' : 'participant1';
    const newScoringScore = Math.max(0, (currentScore[participant] || 0) + delta);
    const otherScore = currentScore[opponent] || 0;
    announce(newScoringScore, otherScore, isCorrection);
  }, [currentScore, announce]);

  // Debug permission state changes
  useEffect(() => {
    debugLog('🔍 Permission Debug:', {
      isReadOnly,
      isReadOnlyMode,
      canEdit: canEditScore(),
      currentUserId,
      userRole,
      matchCreator: match?.created_by || match?.createdBy,
      participant1: match?.participant_1_id || match?.participant1Id,
      participant2: match?.participant_2_id || match?.participant2Id,
      isAuthenticated
    });
  }, [isReadOnlyMode, isReadOnly, user, match, currentUserId, userRole, isAuthenticated, canEditScore]);

  useLiveMatchWebSocket({
    matchId,
    numericMatchId,
    socket,
    connected,
    connectionState,
    connect,
    joinMatch,
    leaveMatch,
    liveScore,
    matchEvents,
    match,
    isReadOnlyMode,
    setEventLog,
    setCurrentScore,
    announce,
    isScoreSyncPending,
    syncServerScore,
    lastScoreBroadcast,
  });

  // Set / game state held by the server (LS-B07): on page load, and from
  // every live score broadcast (another scorer, or this page as a viewer).
  useEffect(() => {
    if (serverGameState) setLiveGameState(serverGameState);
  }, [serverGameState]);
  useEffect(() => {
    if (!liveScore) return;
    const gs = isReadOnlyMode ? gameStateFromScoreData(liveScore) : adoptServerState(liveScore);
    if (gs) setLiveGameState(gs);
  }, [liveScore, isReadOnlyMode, adoptServerState]);

  // Timeout is a LOCAL operation — it pauses the scoreboard clock without
  // requiring an active WebSocket connection. If the match is live and
  // connected, we also fire-and-forget a timeline event for record-keeping.
  const handleTimeout = () => {
    if (isTimeoutActive) return;
    setIsTimeoutActive(true);
    stopTimer();
    if (isMatchActive && connected) {
      handleSendEvent("timeout", {
        description: "Timeout called - match paused",
        enhancedDescription: "⏸️ Match paused due to timeout",
      }).catch((err) => console.error("Timeout event failed:", err));
    }
  };

  const handleResumeTimeout = () => {
    if (!isTimeoutActive) return;
    setIsTimeoutActive(false);
    if (isMatchActive && connected) {
      handleSendEvent("timeout_end", {
        description: "Play resumed - timeout ended",
        enhancedDescription: "▶️ Match resumed - play continues",
      }).catch((err) => console.error("Timeout end event failed:", err));
    }
  };

  // Wrapper for handleCompleteMatch that uses custom confirm modal
  const handleScoreDecreaseWithAnnounce = useCallback((participant, points = 1) => {
    announceScoreChange(participant, -points, true);
    handleScoreDecrease(participant, points);
  }, [announceScoreChange, handleScoreDecrease]);

  const handleResetScoreWithModal = () => {
    setConfirmModal({
      isOpen: true,
      title: '⚠️ Reset Score',
      message: 'Are you sure you want to reset the score to 0 – 0? This cannot be undone.',
      confirmText: 'Reset',
      cancelText: 'Cancel',
      isDanger: true,
      onConfirm: () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        // Only the score goes back to 0-0; the match clock keeps running (LS-B09)
        handleResetScore();
      },
      onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
    });
  };

  // ── Multi-game state sync (badminton Bo3/Bo5, etc.) ──────────────────────
  // When the scorer completes a game the new state is broadcast to viewers via
  // a WebSocket "game_state_update" event so their counters stay in sync.
  const handleGameStateChange = useCallback((gameState) => {
    setLiveGameState(gameState);
    // Stored with the next score write (the new set's 0-0) - LS-B07
    setScoreContext({ gameState });
    if (connected && isMatchActive) {
      handleSendEvent("game_state_update", {
        ...gameState,
        period: gameState.currentGame,
      }).catch(() => {});
    }
  }, [connected, handleSendEvent, isMatchActive, setScoreContext]);

  // Timed boards report the quarter / half so events and the line score carry it
  const handlePeriodChange = useCallback((info) => setScoreContext(info), [setScoreContext]);

  // Viewers: extract game state from incoming WebSocket match events
  useEffect(() => {
    if (!matchEvents || matchEvents.length === 0) return;
    const event = matchEvents.find((e) => {
      const t = e.eventType || e.event_type || e.type;
      return t === "game_state_update";
    });
    if (!event) return;
    const data = event.eventData || event.data || event;
    if (data.gamesWon && Array.isArray(data.gameScores) && data.currentGame != null) {
      setLiveGameState({
        gamesWon: data.gamesWon,
        gameScores: data.gameScores,
        currentGame: data.currentGame,
        matchWon: data.matchWon ?? false,
      });
    }
  }, [matchEvents]);

  // Keep a live ref so handleAdvancedScoreConfirm never reads a stale participant
  const advancedModalRef = useRef(advancedModal);
  advancedModalRef.current = advancedModal;

  // triggerWinValidation is defined after handleCompleteMatchWithModal (ordering
  // constraint). A ref lets handleAdvancedScoreConfirm call it without a TDZ
  // violation and without needing it in the dep array.
  const triggerWinValidationRef = useRef(null);

  // Advanced scoring confirmation: player + metric selected in modal
  // extraPlayers: for block-assist flow, the assisting player(s) with no score credit
  const handleAdvancedScoreConfirm = useCallback((player, metricKey, metric, extraPlayers = []) => {
    const { participant, scorePoints = 1 } = advancedModalRef.current;
    setAdvancedModal({ isOpen: false, participant: null, preSelectedPlayer: null });
    if (!participant) return;

    const opponent = participant === 'participant1' ? 'participant2' : 'participant1';
    // Drop-in teams are ephemeral (no row in the teams table), so pass null.
    // For user-vs-user (non-team) matches, participant IDs are user IDs —
    // never pass them as teamId because player_stats has a teams FK.
    const dit = (match?.matchConfig || match?.match_config)?.drop_in_teams;
    const p1Type = match?.participant1Type || match?.participant_1_type;
    const p2Type = match?.participant2Type || match?.participant_2_type;
    const participantType = participant === 'participant1' ? p1Type : p2Type;
    const opponentType   = opponent   === 'participant1' ? p1Type : p2Type;
    const teamId = (!dit && participantType === 'team')
      ? (participant === 'participant1'
          ? (match?.participant1Id || match?.participant_1_id)
          : (match?.participant2Id || match?.participant_2_id))
      : null;
    const opponentTeamId = (!dit && opponentType === 'team')
      ? (opponent === 'participant1'
          ? (match?.participant1Id || match?.participant_1_id)
          : (match?.participant2Id || match?.participant_2_id))
      : null;

    const buildPayload = (p) => ({
      metricKey,
      metricLabel: metric.label,
      affectsScore: metric.affectsScore,
      isPositive: metric.isPositive,
      scorePoints: metric.scorePoints || scorePoints,
      playerName: p?.name || p?.full_name || p?.username || 'Player',
      playerUserId: p?.id,
      teamId,
      playerPosition: p?.position,
      jerseyNumber: p?.jerseyNumber ?? p?.jersey_number ?? null,
      ...(metric.typeField && metric.typeKey
        ? { [metric.typeField]: metric.typeKey, typeLabel: metric.typeLabel }
        : {}),
    });

    if (metric.awardsOpponentPoint) {
      // Service Error / Foul: stat on the player who faulted, +1 goes to opponent.
      // The opponent's point is linked to the fault so undoing it undoes both (LS-B11).
      const faultEventId = newClientEventId('adv');
      const faultPayload = buildPayload(player);
      handleAdvancedScoreUpdate(participant, { ...faultPayload, affectsScore: false, clientEventId: faultEventId });
      handleAdvancedScoreUpdate(opponent, {
        metricKey:   `${metricKey}_conceded`,
        metricLabel: `Point (${metric.label})`,
        affectsScore: true,
        scorePoints:  1,
        isPositive:   true,
        playerName:   '—',
        playerUserId: null,
        teamId:       opponentTeamId,
        playerPosition: null,
        jerseyNumber:   null,
        linkedEvent: faultPayload.playerUserId
          ? { playerUserId: faultPayload.playerUserId, metricKey, clientEventId: faultEventId }
          : { clientEventId: faultEventId },
      });
      announceScoreChange(opponent, 1, false);
      // Check win condition for the opponent who just received the point
      triggerWinValidationRef.current?.(opponent);
    } else {
      // Normal action: record for the playing team
      handleAdvancedScoreUpdate(participant, buildPayload(player));
      if (metric.affectsScore) {
        announceScoreChange(participant, metric.scorePoints || scorePoints, false);
        triggerWinValidationRef.current?.(participant);
      }

      // Block-assist extraPlayers: record stat only (no score)
      extraPlayers.forEach((ep) => {
        handleAdvancedScoreUpdate(participant, {
          ...buildPayload(ep),
          metricKey:   'blockAssist',
          metricLabel: 'Block (Assist)',
          affectsScore: false,
        });
      });
    }
  }, [handleAdvancedScoreUpdate, match, announceScoreChange]);

  // Direct inline action from the scoreboard (advanced mode chips).
  // Bypasses the two-step modal entirely — participant and player are already known.
  const handleDirectAdvancedAction = useCallback((participant, metricKey, metric, player) => {
    advancedModalRef.current = { participant, scorePoints: metric.scorePoints || 1 };
    handleAdvancedScoreConfirm(player, metricKey, metric);
  }, [handleAdvancedScoreConfirm]);

  // Inline player icon tapped in advanced mode → action popover beside the icon.
  // Tapping another icon switches in one tap; tapping the same icon closes it.
  const handlePlayerSelectForAdvanced = useCallback((participant, player, anchorEl) => {
    const anchorKey = anchorEl?.getAttribute?.('data-player-anchor');
    if (!anchorKey) {
      setAdvancedModal({ isOpen: true, participant, preSelectedPlayer: player, scorePoints: 1 });
      return;
    }
    setSwapState(null);
    setPopover((prev) => (prev?.anchorKey === anchorKey ? null : { participant, player, anchorKey }));
  }, []);

  const closePopover = useCallback(() => setPopover(null), []);

  const popoverRef = useRef(popover);
  popoverRef.current = popover;

  // Popover action → same recording path as the modal (handleAdvancedScoreConfirm)
  const handlePopoverConfirm = useCallback((player, metricKey, metric, extraPlayers = []) => {
    const participant = popoverRef.current?.participant;
    setPopover(null);
    if (!participant) return;
    advancedModalRef.current = { participant, scorePoints: metric.scorePoints || 1 };
    handleAdvancedScoreConfirm(player, metricKey, metric, extraPlayers);
  }, [handleAdvancedScoreConfirm]);

  // ── Substitutions ─────────────────────────────────────────────────────────────
  /** The limit message when this side cannot make another substitution, else null. */
  const subLimitMessage = useCallback((participant) => {
    if (subRules.maxPerSet != null && (lineups[participant]?.subsThisSet || 0) >= subRules.maxPerSet) {
      return `Substitution limit reached (${subRules.maxPerSet} per set)`;
    }
    if (subRules.maxPerMatch != null && countSubstitutions(lineups[participant]) >= subRules.maxPerMatch) {
      return `Substitution limit reached (${subRules.maxPerMatch} per match)`;
    }
    return null;
  }, [subRules.maxPerSet, subRules.maxPerMatch, lineups]);

  const doSubstitute = useCallback(async (participant, outId, inId) => {
    setSwapState(null);
    const score = `${currentScore?.participant1 || 0}-${currentScore?.participant2 || 0}`;
    // Substitution time (AS-B06): the period and the match clock
    const clockMs = currentClockMs();
    const meta = { score, ...(clockState?.period ? { period: clockState.period } : {}), ...(clockMs !== null ? { clockMs } : {}) };
    const result = await substitute(participant, outId, inId, meta);
    if (!result.allowed) {
      showLineupMessage(participant, result.reason);
      return;
    }
    const name = (p) => p?.name || p?.full_name || p?.username || 'Player';
    handleSubstitutionEvent(participant, {
      inId,
      outId,
      inName: name(findPlayer(participant, inId)),
      outName: name(findPlayer(participant, outId)),
      teamName: getParticipantName(match, participant),
      period: meta.period ?? result.lineup?.set,
      clockMs: meta.clockMs,
    });
  }, [currentScore, substitute, showLineupMessage, handleSubstitutionEvent, findPlayer, match, currentClockMs, clockState]);

  const handleBenchTap = useCallback((participant, player) => {
    const pid = player.id ?? player.userId;
    setPopover(null);
    if (swapState?.participant === participant && swapState.outId != null) {
      doSubstitute(participant, swapState.outId, pid);
      return;
    }
    if (swapState?.participant === participant && String(swapState.inId) === String(pid)) {
      setSwapState(null);
      return;
    }
    const limit = subLimitMessage(participant);
    if (limit) {
      setSwapState(null);
      showLineupMessage(participant, limit);
      return;
    }
    setSwapState({ participant, inId: pid });
  }, [swapState, doSubstitute, subLimitMessage, showLineupMessage]);

  const handleCourtSwapTap = useCallback((participant, player) => {
    const pid = player.id ?? player.userId;
    if (swapState?.participant !== participant) return;
    if (swapState.inId != null) {
      doSubstitute(participant, pid, swapState.inId);
      return;
    }
    setSwapState(String(swapState.outId) === String(pid) ? null : { participant, outId: pid });
  }, [swapState, doSubstitute]);

  const handleSubOut = useCallback((participant, player) => {
    setPopover(null);
    const limit = subLimitMessage(participant);
    if (limit) {
      showLineupMessage(participant, limit);
      return;
    }
    setSwapState({ participant, outId: player.id ?? player.userId });
  }, [subLimitMessage, showLineupMessage]);

  // Tap anywhere outside the players, or Escape, cancels a swap in progress
  useEffect(() => {
    if (!swapState) return undefined;
    const onPointerDown = (e) => {
      if (!e.target?.closest?.('[data-swap-zone]')) setSwapState(null);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setSwapState(null); };
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [swapState]);

  // "⇄ Sub out" in the popover only when someone on the bench may replace this player
  const canSubOut = (participant, player) => {
    const lineup = lineups[participant];
    if (!canSwap || !lineup) return false;
    const pid = player?.id ?? player?.userId;
    return lineup.bench.some((benchId) => canSubstitute(lineup, pid, benchId, { ...subRules, maxPerSet: null, maxPerMatch: null }).allowed);
  };

  // New volleyball set: the per-set substitution counter starts again
  const handleResetSetScore = useCallback((options) => {
    if (options?.eventType === 'game_start') startNewSet(options.period);
    return handleResetScore(options);
  }, [startNewSet, handleResetScore]);

  const lineupContextValue = {
    rules: subRules,
    getLineup: (participant) => lineups[participant] || null,
    canSwap,
    swap: swapState,
    message: lineupMessage,
    activeAnchorKey: popover?.anchorKey || null,
    onBenchTap: handleBenchTap,
    onCourtSwapTap: handleCourtSwapTap,
    canSubstitute: (participant, outId, inId) => canSubstitute(lineups[participant], outId, inId, subRules),
  };

  // Wrapper for handleScoreUpdate with scoring-rules validation
  const handleScoreIncreaseWithValidation = (participant, points = 1) => {
    // In Advanced mode with inline players: the scoreboard shows players on-card;
    // the +1 button is hidden. This path is only reached if there are no players
    // loaded yet — fall through to the legacy modal (step 1 visible) as a fallback.
    if (scoringMode === 'advanced' && advancedSupported) {
      setAdvancedModal({ isOpen: true, participant, preSelectedPlayer: null, scorePoints: points });
      return;
    }

    // Multi-set sports (volleyball, badminton, tennis, etc.) manage their own
    // set transitions, win detection, and match completion through the sport-
    // specific scoreboard component.  Skip global win validation here to prevent
    // a premature "Complete Match" dialog on every non-final set win.
    // Volleyball / badminton / table tennis boards also handle single-set
    // matches themselves (e.g. volleyball has no points cap, so the generic
    // max-points block must never apply).
    const gameRules = (match?.matchConfig || match?.match_config)?.game_rules || {};
    if ((gameRules.total_games ?? 1) > 1 || sportManagesOwnGames(sportName)) {
      announceScoreChange(participant, points, false);
      handleScoreUpdate(participant, points);
      return;
    }

    const validation = validateScoreIncrease(participant, currentScore, match);

    if (!validation) {
      // No issue — score normally
      announceScoreChange(participant, points, false);
      handleScoreUpdate(participant, points);
      return;
    }

    if (validation.type === "max_blocked") {
      // Hard block — show info modal, don't score
      setConfirmModal({
        isOpen: true,
        title: '🔝 Maximum Score Reached',
        message: validation.message,
        confirmText: 'OK',
        cancelText: null,
        isDanger: false,
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel:  () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }

    if (validation.type === "win" || validation.type === "cap_no_margin") {
      // Win / cap reached — score first, then offer to complete
      announceScoreChange(participant, points, false);
      handleScoreUpdate(participant, points);
      setConfirmModal({
        isOpen: true,
        title: validation.type === "cap_no_margin" ? '🔝 Score Cap Reached' : '🏆 Win Condition Met!',
        message: validation.message,
        confirmText: 'Complete Match',
        cancelText: 'Keep Playing',
        isDanger: false,
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          handleCompleteMatchWithModal();
        },
        onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }

    // Fallback — just score
    announceScoreChange(participant, points, false);
    handleScoreUpdate(participant, points);
  };
  const handleCompleteMatchWithModal = async (completionData = null) => {
    try {
      // Multi-game sports (e.g. badminton Bo3/Bo5, volleyball) pass a
      // pre-resolved completionData with the match winner already determined.
      // Show a winner-confirmation popup before actually completing so users
      // always get to review and can cancel if the button was hit by mistake.
      if (completionData && !completionData.cancelled) {
        const p1Score = completionData.participant1Score ?? 0;
        const p2Score = completionData.participant2Score ?? 0;
        const winnerLine = completionData.winnerName
          ? `Winner: ${completionData.winnerName}`
          : '🤝 Draw';
        const scoreLabel = completionData.gameScores
          ? `Games: ${p1Score}–${p2Score}`          // e.g. badminton games-won
          : `Score: ${p1Score}–${p2Score}`;

        const confirmed = await new Promise((resolve) => {
          setConfirmModal({
            isOpen:      true,
            title:       '🏆 Complete Match',
            message:     `${winnerLine}\n${scoreLabel}`,
            confirmText: 'Complete Match',
            cancelText:  'Cancel',
            isDanger:    false,
            onConfirm: () => { setConfirmModal(prev => ({ ...prev, isOpen: false })); resolve(true); },
            onCancel:  () => { setConfirmModal(prev => ({ ...prev, isOpen: false })); resolve(false); },
          });
        });
        if (!confirmed) return; // user cancelled
        await handleCompleteMatch(completionData);
        return;
      }

      const participant1Score = currentScore?.participant1 || 0;
      const participant2Score = currentScore?.participant2 || 0;

      // Use the same name resolver as the scoreboard so they always match
      const participant1Name = getParticipantName(match, "participant1");
      const participant2Name = getParticipantName(match, "participant2");

      // Respect participant type (team vs individual)
      const p1Type = match?.participant1Type || match?.participant_1_type || "user";
      const p2Type = match?.participant2Type || match?.participant_2_type || "user";
      const p1Id   = match?.participant1Id   || match?.participant_1_id;
      const p2Id   = match?.participant2Id   || match?.participant_2_id;

      let winner;
      let winnerName;

      // Determine winner by score; equal scores → ask user for draw/override
      if (participant1Score > participant2Score) {
        winner = { id: p1Id, type: p1Type };
        winnerName = participant1Name;
      } else if (participant2Score > participant1Score) {
        winner = { id: p2Id, type: p2Type };
        winnerName = participant2Name;
      } else {
        // Tie - show dedicated modal with Draw, p1 winner, p2 winner, or Cancel
        const tieChoice = await new Promise((resolve) => {
          setTieModal({
            isOpen: true,
            p1Name: participant1Name,
            p2Name: participant2Name,
            score: `${participant1Score}–${participant2Score}`,
            // Basketball, volleyball, racket sports: no draws (e.g. FIBA —
            // a tied game goes to overtime).
            allowDraw: sportAllowsDraw(sportName),
            onResolve: (choice) => {
              setTieModal(prev => ({ ...prev, isOpen: false }));
              resolve(choice);
            },
          });
        });

        if (tieChoice === 'cancel') return; // user cancelled — don't complete

        if (tieChoice === 'draw' && sportAllowsDraw(sportName)) {
          winner = null;
          winnerName = 'Draw';
        } else if (tieChoice === 'p1') {
          winner = { id: p1Id, type: p1Type };
          winnerName = participant1Name;
        } else {
          winner = { id: p2Id, type: p2Type };
          winnerName = participant2Name;
        }
      }

      // Final confirmation — shows who won before completing
      await new Promise((resolve, reject) => {
        setConfirmModal({
          isOpen: true,
          title: '🏆 Complete Match',
          message: `Winner: ${winnerName}\nFinal Score: ${participant1Score}–${participant2Score}`,
          confirmText: 'Complete Match',
          cancelText: 'Cancel',
          isDanger: false,
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            resolve();
          },
          onCancel: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
            reject();
          }
        });
      });

      // Actually complete the match
      await handleCompleteMatch({
        cancelled: false,
        winner,
        winnerName,
        participant1Score,
        participant2Score,
      });
      
    } catch (err) {
      // User cancelled
      debugLog('Match completion cancelled');
    }
  };

  /**
   * Run win-condition check for the participant who just received a point in
   * advanced scoring. For multi-game sports (badminton Bo3/Bo5, volleyball, etc.)
   * the sport-specific scoreboard handles win detection internally; skip here.
   * For single-game matches validateScoreIncrease predicts the post-increment
   * result using the current (pre-update) score — correct because the WebSocket
   * score update is asynchronous.
   */
  const triggerWinValidation = useCallback((participant) => {
    const gameRules = (match?.matchConfig || match?.match_config)?.game_rules || {};
    const totalGames = gameRules.total_games ?? 1;

    // For multi-game sports: if a dedicated sport scoreboard is registered,
    // that component shows an inline "Complete Game N" banner and handles
    // game transitions internally — skip the popup to avoid conflict.
    // If no dedicated scoreboard is registered (TwoParticipantScoreboard
    // fallback), fall through and still show a popup so the user gets feedback.
    const hasDedicatedScoreboard = getSportScoreboardComponent(sportName) !== null;
    if (totalGames > 1 && hasDedicatedScoreboard) return;
    if (sportManagesOwnGames(sportName)) return;

    const validation = validateScoreIncrease(participant, currentScore, match);
    if (!validation) return;

    if (validation.type === 'max_blocked') {
      setConfirmModal({
        isOpen: true,
        title: '🔝 Maximum Score Reached',
        message: validation.message,
        confirmText: 'OK',
        cancelText: null,
        isDanger: false,
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel:  () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }

    if (validation.type === 'win' || validation.type === 'cap_no_margin') {
      setConfirmModal({
        isOpen: true,
        title: validation.type === 'cap_no_margin' ? '🔝 Score Cap Reached' : '🏆 Win Condition Met!',
        message: validation.message,
        confirmText: 'Complete Match',
        cancelText: 'Keep Playing',
        isDanger: false,
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          handleCompleteMatchWithModal();
        },
        onCancel: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
    }
  }, [match, currentScore, sportName, handleCompleteMatchWithModal]);

  // Keep ref in sync so handleAdvancedScoreConfirm always gets the latest version
  triggerWinValidationRef.current = triggerWinValidation;

  // Detect if this is being used as a full page or as a modal
  const isFullPage = !onClose;

  if (loading) {
    return (
      <GameLayout>
        <div className="live-match-gaming-layout">
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-1">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>🎮 Loading Live Match</h3>
                <p>Connecting to match arena...</p>
                {isFullPage && (
                  <FloatingButton
                    onClick={doNavigateBack}
                    variant="secondary"
                    size="medium"
                    icon="←"
                  >
                    Back to Matches
                  </FloatingButton>
                )}
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!match) {
    return (
      <GameLayout>
        <div className="live-match-gaming-layout">
          <div className="error-gaming-floating">
            <FloatingCard variant="danger" size="large" className="delay-1">
              <div className="error-content-gaming">
                <div className="error-icon">❌</div>
                <h3>Match Not Found</h3>
                <p>The requested match could not be loaded.</p>
                <div className="error-actions">
                  <FloatingButton
                    onClick={isFullPage ? doNavigateBack : onClose}
                    variant="secondary"
                    size="medium"
                    icon="←"
                  >
                    {isFullPage ? "Back to Matches" : "Close"}
                  </FloatingButton>
                </div>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
    >
      <div className="live-match-gaming-layout">
        {/* Header Section */}
        <LiveMatchHeader
          isFullPage={isFullPage}
          match={match}
          isMatchActive={isMatchActive}
          onBack={handleBackWithDialog}
          onClose={onClose}
          sportName={match?.sport_name || match?.sportName}
        />

        {/* A captain's final result waits for the other team (accept / decline on the match page) */}
        {match?.viewer?.pendingResult && (
          <div className="error-alert-gaming" data-testid="live-pending-result">
            <FloatingCard size="medium">
              <div className="alert-content-gaming">
                <span>
                  {match.viewer.pendingResult.isProposer
                    ? `⏳ Result sent: waiting for ${match.viewer.pendingResult.awaitingSide === "participant1"
                      ? (match.participant_1_name || match.participant1Name || "the other side")
                      : match.viewer.pendingResult.awaitingSide === "participant2"
                        ? (match.participant_2_name || match.participant2Name || "the other side")
                        : "both sides"} to confirm`
                    : "📝 The other side sent the final result: accept or decline it on the match page"}
                </span>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="error-alert-gaming">
            <FloatingCard variant="danger" size="medium" className="delay-2">
              <div className="alert-content-gaming">
                <span className="alert-icon">⚠️</span>
                <span className="alert-message">{error}</span>
                <FloatingButton
                  onClick={() => setError(null)}
                  variant="secondary"
                  size="small"
                  icon="×"
                >
                  Dismiss
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        <div className="live-match-content-gaming">
          {/* Live Score Display */}
          <LineupContext.Provider value={lineupContextValue}>
          <ScoreFullscreen enabled={!/basketball/i.test(match?.sport_name || "")}>
          <LiveScoreboard
            match={match}
            currentScore={currentScore}
            isReadOnlyMode={isReadOnlyMode}
            connected={connected}
            isMatchActive={isMatchActive}
            onScoreIncrease={handleScoreIncreaseWithValidation}
            onScoreDecrease={handleScoreDecreaseWithAnnounce}
            onResetScore={handleResetScoreWithModal}
            onResetSetScore={handleResetSetScore}
            onToggleTimeout={isTimeoutActive ? handleResumeTimeout : handleTimeout}
            onCompleteMatch={handleCompleteMatchWithModal}
            onStartMatch={handleStartMatch}
            matchDuration={matchDuration}
            isTimeoutActive={isTimeoutActive}
            scoringRules={getMatchScoringRules(match)}
            scoringMode={scoringMode}
            onScoringModeChange={setScoringMode}
            advancedSupported={advancedSupported}
            announcerMuted={muted}
            onAnnouncerToggleMute={toggleMute}
            onAnnouncerRepeat={repeat}
            onGameComplete={handleGameStateChange}
            externalGameState={liveGameState}
            serverClock={clockState}
            onClockChange={postClock}
            onPeriodChange={handlePeriodChange}
            quickMetrics={advancedSupported ? (sportScorer?.getQuickMetrics ? sportScorer.getQuickMetrics(null) : []) : []}
            p1Players={p1Players}
            p2Players={p2Players}
            onDirectAction={handleDirectAdvancedAction}
            onPlayerSelect={scoringMode === 'advanced' && advancedSupported ? handlePlayerSelectForAdvanced : undefined}
          />
          </ScoreFullscreen>
          </LineupContext.Provider>

          {/* WebSocket reconnect hint — only shown to editors after max retries */}
          {!isReadOnlyMode && !connected && connectionState === 'error' && (
            <FloatingCard variant="outline" size="medium" className="connection-warning-banner delay-4">
              <div className="warning-content">
                <span>⚠️ Real-time sync unavailable — scores update every 5 s</span>
                <FloatingButton
                  onClick={() => connect()}
                  variant="info"
                  size="small"
                  icon="🔌"
                >
                  Reconnect
                </FloatingButton>
              </div>
            </FloatingCard>
          )}

          {/* Host-only: Cancel / Forfeit actions */}
          {!isReadOnlyMode && canEditScore() && isMatchActive && (
            <FloatingCard variant="outline" size="medium" className="delay-5" style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', gap: '0.65rem', justifyContent: 'center', flexWrap: 'wrap', padding: '0.25rem 0' }}>
                <button
                  onClick={handleForfeitWithModal}
                  style={{ padding: '0.45rem 1.1rem', fontSize: '0.8rem', background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.5)', color: '#f59e0b', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600 }}
                >
                  🏳️ Forfeit
                </button>
                <button
                  onClick={handleCancelMatchWithModal}
                  style={{ padding: '0.45rem 1.1rem', fontSize: '0.8rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.45)', color: '#f87171', borderRadius: '0.4rem', cursor: 'pointer', fontWeight: 600 }}
                >
                  ❌ Cancel Match
                </button>
              </div>
            </FloatingCard>
          )}

          {/* Live Event Feed */}
          <LiveEventFeed
            eventLog={eventLog}
            isMatchActive={isMatchActive}
            connected={connected}
            match={match}
          />

          {/* WebSocket Match Events from Context - DISABLED to prevent duplicate display */}
          {false && matchEvents && matchEvents.length > 0 && (
            <FloatingCard
              variant="secondary"
              size="medium"
              className="websocket-events delay-6"
            >
              <div className="ws-events-header">
                <h3>🔄 WebSocket Events</h3>
                <span className="events-count">{matchEvents.length} events</span>
              </div>
              
              <div className="ws-events-list">
                {matchEvents.slice(0, 10).map((event, index) => (
                  <div key={index} className="ws-event-item">
                    <div className="ws-event-data">
                      {event.eventData?.description || 
                       event.eventData?.enhancedDescription || 
                       event.description || 
                       (event.eventData?.participantName ? 
                        `${event.eventType || event.type} - ${event.eventData.participantName}` :
                        JSON.stringify(event.eventData || event.data))}
                    </div>
                    <div className="ws-event-time">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            </FloatingCard>
          )}


        </div>

      </div>

      {/* Event Form Modal */}
      <LiveMatchEventModal
        show={showEventForm}
        eventData={eventData}
        setEventData={setEventData}
        scoringTemplate={scoringTemplate}
        match={match}
        getEventTypes={getEventTypes}
        onSubmit={handleAddEvent}
        onClose={() => setShowEventForm(false)}
        connected={connected}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={async () => {
          if (confirmModal.onConfirm) {
            await confirmModal.onConfirm();
          }
        }}
        onCancel={() => {
          if (confirmModal.onCancel) {
            confirmModal.onCancel();
          }
        }}
        isDanger={confirmModal.isDanger}
      />

      {/* Tie-Breaker Modal */}
      {tieModal.isOpen && (
        <div className="confirm-modal-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3 className="confirm-modal-title">🤝 Match is Tied</h3>
            </div>
            <p className="confirm-modal-text">
              Score is tied {tieModal.score}.<br/>
              {tieModal.allowDraw === false
                ? 'This sport has no draws — play overtime / a tie-break, or pick the winner.'
                : 'Declare a draw, pick a winner, or cancel.'}
            </p>
            <div className="confirm-modal-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" className="confirm-btn cancel"
                onClick={() => tieModal.onResolve('cancel')}>
                Cancel
              </button>
              {tieModal.allowDraw !== false && (
                <button type="button" className="confirm-btn"
                  style={{ background: 'linear-gradient(135deg, #475569, #334155)', color: '#e2e8f0' }}
                  onClick={() => tieModal.onResolve('draw')}>
                  🤝 Draw
                </button>
              )}
              <button type="button" className="confirm-btn"
                onClick={() => tieModal.onResolve('p1')}>
                🏆 {tieModal.p1Name}
              </button>
              <button type="button" className="confirm-btn"
                onClick={() => tieModal.onResolve('p2')}>
                🏆 {tieModal.p2Name}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forfeit Modal — pick which team is forfeiting */}
      {forfeitModal.isOpen && (
        <div className="confirm-modal-overlay" role="dialog" aria-modal="true">
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3 className="confirm-modal-title">🏳️ Record Forfeit</h3>
            </div>
            <p className="confirm-modal-text">Which team is forfeiting?</p>
            <div className="confirm-modal-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" className="confirm-btn cancel"
                onClick={() => setForfeitModal({ isOpen: false })}>
                Cancel
              </button>
              <button type="button" className="confirm-btn"
                style={{ background: 'linear-gradient(135deg, #ef444444, #b91c1c)', color: '#fee2e2' }}
                onClick={() => { setForfeitModal({ isOpen: false }); handleForfeitMatch('participant1'); }}>
                🏳️ {getParticipantName(match, 'participant1')} forfeits
              </button>
              <button type="button" className="confirm-btn"
                style={{ background: 'linear-gradient(135deg, #ef444444, #b91c1c)', color: '#fee2e2' }}
                onClick={() => { setForfeitModal({ isOpen: false }); handleForfeitMatch('participant2'); }}>
                🏳️ {getParticipantName(match, 'participant2')} forfeits
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Scoring Modal */}
      <AdvancedScoringModal
        isOpen={advancedModal.isOpen}
        onClose={() => setAdvancedModal({ isOpen: false, participant: null, preSelectedPlayer: null })}
        onConfirm={handleAdvancedScoreConfirm}
        onDecrease={() => handleScoreDecreaseWithAnnounce(advancedModal.participant)}
        players={courtPlayers(advancedModal.participant)}
        preSelectedPlayer={advancedModal.preSelectedPlayer || null}
        teamLabel={
          advancedModal.participant === 'participant1'
            ? getParticipantName(match, 'participant1')
            : getParticipantName(match, 'participant2')
        }
        scorer={sportScorer}
        participant={advancedModal.participant}
        sportKey={sportName}
      />

      {/* Action popover beside the tapped player icon */}
      {popover && (
        <PlayerActionPopover
          key={popover.anchorKey}
          anchorKey={popover.anchorKey}
          player={popover.player}
          participant={popover.participant}
          scorer={sportScorer}
          teammates={courtPlayers(popover.participant)}
          onConfirm={handlePopoverConfirm}
          onClose={closePopover}
          onSubOut={canSubOut(popover.participant, popover.player) ? handleSubOut : undefined}
        />
      )}

      {/* Back-navigation dialog — shown when the match clock is running */}
      {backNavModal.isOpen && (
        <div className="confirm-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="back-nav-title">
          <div className="confirm-modal">
            <div className="confirm-modal-header">
              <h3 className="confirm-modal-title" id="back-nav-title">⏱️ Match In Progress</h3>
            </div>
            <p className="confirm-modal-text">
              The match clock is running. What would you like to do before leaving?
            </p>
            <div className="confirm-modal-actions back-nav-modal__actions">
              <button
                type="button"
                className="confirm-btn cancel"
                onClick={() => setBackNavModal({ isOpen: false })}
              >
                Stay
              </button>
              <button
                type="button"
                className="confirm-btn"
                style={{ background: 'linear-gradient(135deg, #f59e0b44, #b45309)', color: '#fef3c7' }}
                onClick={() => {
                  setBackNavModal({ isOpen: false });
                  handleTimeout();
                  doNavigateBack();
                }}
              >
                ⏸️ Timeout &amp; Leave
              </button>
              <button
                type="button"
                className="confirm-btn"
                style={{ background: 'linear-gradient(135deg, #6366f144, #4338ca)', color: '#e0e7ff' }}
                onClick={() => {
                  setBackNavModal({ isOpen: false });
                  doNavigateBack();
                }}
              >
                ▶️ Keep Running &amp; Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </GameLayout>
  );
};

export default LiveMatch;
